/**
 * Read-only queries for the Monthly Board History section.
 *
 * Everything is read straight from the finalized snapshot rows, so opening a
 * history page never recomputes a ranking. All queries run in route loaders
 * (server-side during SSR) so the ranking HTML is crawlable.
 */
import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { HistoryEntry, HistoryPeriod, HistorySnapshot } from "@/lib/history";

const LONG = 30 * 60 * 1000;

const PERIOD_FIELDS =
  "id,year,month,status,board_count,entry_count,total_heat_cents,finalized_at";
const SNAPSHOT_FIELDS =
  "id,period_id,year,month,category_id,category_slug,category_name,category_description,parent_slug,parent_name,entry_count,total_heat_cents,is_public,finalized_at";
const ENTRY_FIELDS =
  "id,snapshot_id,year,month,category_slug,category_name,position,listing_id,listing_name,listing_slug,listing_headline,listing_description,listing_path,external_url,image_url,owner_user_id,owner_slug,owner_name,owner_path,heat_cents,booster_count,click_count,country_code,badges";

/** Every recorded month, newest first. */
export const historyPeriodsQuery = queryOptions({
  queryKey: ["history", "periods"],
  staleTime: LONG,
  queryFn: async (): Promise<HistoryPeriod[]> => {
    const { data, error } = await supabase
      .from("board_history_periods")
      .select(PERIOD_FIELDS)
      .in("status", ["finalized"])
      .order("year", { ascending: false })
      .order("month", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as HistoryPeriod[];
  },
});

/** All publicly visible boards recorded for one month. */
export function historyMonthQuery(year: number, month: number) {
  return queryOptions({
    queryKey: ["history", "month", year, month],
    staleTime: LONG,
    queryFn: async (): Promise<HistorySnapshot[]> => {
      const { data, error } = await supabase
        .from("board_history_snapshots")
        .select(SNAPSHOT_FIELDS)
        .eq("year", year)
        .eq("month", month)
        .eq("is_public", true)
        .order("total_heat_cents", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as HistorySnapshot[];
    },
  });
}

export interface HistoryBoardResult {
  snapshot: HistorySnapshot | null;
  entries: HistoryEntry[];
  total: number;
}

/** One historical board, paginated (SEO-friendly ?page=N, never infinite scroll). */
export function historyBoardQuery(
  year: number,
  month: number,
  board: string,
  page = 1,
  pageSize = 50,
) {
  return queryOptions({
    queryKey: ["history", "board", year, month, board, page, pageSize],
    staleTime: LONG,
    queryFn: async (): Promise<HistoryBoardResult> => {
      const { data: snap, error } = await supabase
        .from("board_history_snapshots")
        .select(SNAPSHOT_FIELDS)
        .eq("year", year)
        .eq("month", month)
        .eq("category_slug", board)
        .eq("is_public", true)
        .maybeSingle();
      if (error) throw error;
      const snapshot = (snap ?? null) as unknown as HistorySnapshot | null;
      if (!snapshot) return { snapshot: null, entries: [], total: 0 };

      const from = (page - 1) * pageSize;
      const { data, error: entryError } = await supabase
        .from("board_history_entries")
        .select(ENTRY_FIELDS)
        .eq("snapshot_id", snapshot.id)
        .order("position", { ascending: true })
        .range(from, from + pageSize - 1);
      if (entryError) throw entryError;
      return {
        snapshot,
        entries: (data ?? []) as unknown as HistoryEntry[],
        total: snapshot.entry_count,
      };
    },
  });
}

/** Recorded achievements for one listing, newest month first. */
export function listingHistoryQuery(listingId: string | undefined) {
  return queryOptions({
    queryKey: ["history", "listing", listingId ?? "none"],
    staleTime: LONG,
    enabled: Boolean(listingId),
    queryFn: async (): Promise<HistoryEntry[]> => {
      if (!listingId) return [];
      const { data, error } = await supabase
        .from("board_history_entries")
        .select(ENTRY_FIELDS)
        .eq("listing_id", listingId)
        .order("year", { ascending: false })
        .order("month", { ascending: false })
        .order("position", { ascending: true })
        .limit(24);
      if (error) throw error;
      return (data ?? []) as unknown as HistoryEntry[];
    },
  });
}

/** Recorded achievements for one user profile, newest month first. */
export function ownerHistoryQuery(ownerUserId: string | undefined) {
  return queryOptions({
    queryKey: ["history", "owner", ownerUserId ?? "none"],
    staleTime: LONG,
    enabled: Boolean(ownerUserId),
    queryFn: async (): Promise<HistoryEntry[]> => {
      if (!ownerUserId) return [];
      const { data, error } = await supabase
        .from("board_history_entries")
        .select(ENTRY_FIELDS)
        .eq("owner_user_id", ownerUserId)
        .lte("position", 10)
        .order("year", { ascending: false })
        .order("month", { ascending: false })
        .order("position", { ascending: true })
        .limit(24);
      if (error) throw error;
      return (data ?? []) as unknown as HistoryEntry[];
    },
  });
}

/** Months in which a given board was recorded — used for "previous rankings". */
export function boardHistoryMonthsQuery(boardSlug: string | undefined) {
  return queryOptions({
    queryKey: ["history", "board-months", boardSlug ?? "none"],
    staleTime: LONG,
    enabled: Boolean(boardSlug),
    queryFn: async (): Promise<HistorySnapshot[]> => {
      if (!boardSlug) return [];
      const { data, error } = await supabase
        .from("board_history_snapshots")
        .select(SNAPSHOT_FIELDS)
        .eq("category_slug", boardSlug)
        .eq("is_public", true)
        .order("year", { ascending: false })
        .order("month", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as unknown as HistorySnapshot[];
    },
  });
}
